// Funciones puras del lote 7 del flujo de un día (pantallas y envío). Sin
// React ni BD, para probarlas con node (scripts/test_pantallas_un_dia.mjs).
//
// - U-32: documento que pinta la ficha tras un guardado (el del servidor) y
//   avisos de la respuesta del PUT.
// - U-69: estado de pago normalizado (mismo criterio que Control de cobros).
// - U-70: pendientes de un evento confirmado con los criterios de Por hacer.
// - U-71 y U-101: lunes de la semana, fecha local y "próximos N días".
// - U-95 y U-102: fechas del servidor guardadas en UTC sin zona.

// ---------------------------------------------------------------------------
// Fechas
// ---------------------------------------------------------------------------

const dos = (n) => String(n).padStart(2, '0');

// 'YYYY-MM-DD' con la fecha LOCAL (no la UTC de toISOString)
export const fechaLocalISO = (fecha = new Date()) => {
  const f = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(f.getTime())) return '';
  return `${f.getFullYear()}-${dos(f.getMonth() + 1)}-${dos(f.getDate())}`;
};

// Fecha que da el servidor. 'YYYY-MM-DD' es un día (00:00 locales); una fecha
// con hora y sin zona se guardó con utcnow(): es UTC (se le añade la Z).
// null si no es una fecha.
export const parsearFechaServidor = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
  const texto = String(valor).trim();
  let f;
  if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
    f = new Date(`${texto}T00:00:00`);
  } else if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(texto) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(texto)) {
    f = new Date(`${texto.replace(' ', 'T')}Z`);
  } else {
    f = new Date(texto);
  }
  return Number.isNaN(f.getTime()) ? null : f;
};

// Lunes (00:00 locales) de la semana de `fecha`; el domingo es el último día
// de la semana (no el primero de la siguiente)
export const lunesDeLaSemana = (fecha = new Date()) => {
  const f = new Date(fecha);
  f.setHours(0, 0, 0, 0);
  f.setDate(f.getDate() - ((f.getDay() + 6) % 7));
  return f;
};

// Días desde hoy (local) hasta el día de `fechaEvento` ('YYYY-MM-DD...'):
// 0 hoy, 1 mañana, negativo si ya pasó. null sin fecha válida.
export const diasHastaFecha = (fechaEvento, hoy = new Date()) => {
  const texto = String(fechaEvento ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return null;
  const [a, m, d] = texto.split('-').map(Number);
  const dia = Date.UTC(a, m - 1, d);
  const h = hoy instanceof Date ? hoy : new Date(hoy);
  const base = Date.UTC(h.getFullYear(), h.getMonth(), h.getDate());
  return Math.round((dia - base) / 86400000);
};

// "Próximos N días": de hoy al día N incluido
export const enProximosDias = (fechaEvento, dias, hoy = new Date()) => {
  const n = diasHastaFecha(fechaEvento, hoy);
  return n !== null && n >= 0 && n <= Number(dias);
};

// ---------------------------------------------------------------------------
// U-69: estado de pago (sin tocar datos: valores antiguos SI, NO, Parcial...)
// ---------------------------------------------------------------------------

export const normalizarEstadoPago = (valor) => {
  const e = String(valor ?? '').trim().toLowerCase();
  if (e === 'pagado' || e === 'si' || e === 'sí') return { clave: 'pagado', texto: 'Pagado', clase: 'bg-emerald-100 text-emerald-700' };
  if (e === 'parcial' || e === 'pago_parcial') return { clave: 'parcial', texto: 'Parcial', clase: 'bg-amber-100 text-amber-700' };
  if (e === 'impagado') return { clave: 'impagado', texto: 'Impagado', clase: 'bg-slate-800 text-white' };
  return { clave: 'pendiente', texto: 'Pendiente', clase: 'bg-red-100 text-red-700' };
};

// ---------------------------------------------------------------------------
// U-70: pendientes de un evento confirmado (criterios de Por hacer)
// ---------------------------------------------------------------------------

const lista = (v) => (Array.isArray(v) ? v.filter(x => x && typeof x === 'object') : []);

const esMulti = (p) => !!p?.es_multi_evento && lista(p?.jornadas).length > 0;

const personasOrden = (orden) => lista(orden?.grupos)
  .reduce((s, g) => s + (parseInt(g.num_personas, 10) > 0 ? parseInt(g.num_personas, 10) : 0), 0);

// Hay orden de personal extra con personas (la del evento o, en multievento
// antiguo, la de alguna jornada)
export const hayOrdenPersonalExtra = (p) => {
  if (personasOrden(p?.orden_personal_extra) > 0) return true;
  return esMulti(p) && lista(p.jornadas).some(j => personasOrden(j.orden_personal_extra) > 0);
};

/**
 * Pendientes de un evento confirmado no celebrado, con los criterios de la
 * pestaña Por hacer (no con los campos antiguos `bebidas_confirmadas`,
 * `camareros_confirmados` ni solo `personal_asignado`):
 * - bebidas: hay bebidas en el evento y el modo de bebidas no está validado;
 * - personal: nadie en Personal asignado ni elegido en Quién va;
 * - orden_personal: Quién va con responsable extra y sin orden de personal extra;
 * - furgonetas: sin furgonetas asignadas;
 * - material: lista de material sin validar (en multievento, alguna jornada);
 * - hora_llegada: sin hora de llegada.
 * Devuelve [{ clave, texto, detalle }].
 */
export const pendientesEventoConfirmado = (p) => {
  if (!p || typeof p !== 'object') return [];
  const out = [];
  const lineas = [...lista(p.menus_personalizados), ...(esMulti(p) ? lista(p.jornadas).flatMap(j => lista(j.menus_personalizados)) : [])];
  const hayBebidas = lineas.some(m => m.bebida_incluida || m.es_bebida) || lista(p.bebidas).length > 0 || !!p.bebidas_modo;
  if (hayBebidas && !p.bebidas_modo_validado_en) {
    out.push({ clave: 'bebidas', texto: 'Bebidas', detalle: 'Falta validar el modo de bebidas' });
  }
  const qv = p.quien_va_evento && typeof p.quien_va_evento === 'object' ? p.quien_va_evento : null;
  const personal = lista(p.personal_asignado);
  const pid = qv?.persona_id;
  const numPersonal = personal.length + (pid && !personal.some(x => x.empleado_id === pid) ? 1 : 0);
  if (numPersonal === 0) {
    out.push({ clave: 'personal', texto: 'Personal', detalle: 'Nadie asignado ni elegido en Quién va' });
  }
  if (qv?.modo === 'extra' && !hayOrdenPersonalExtra(p)) {
    out.push({ clave: 'orden_personal', texto: 'Personal extra', detalle: 'Falta la orden de personal extra' });
  }
  if (lista(p.vehiculos_asignados).length === 0) {
    out.push({ clave: 'furgonetas', texto: 'Furgonetas', detalle: 'Sin furgonetas asignadas' });
  }
  const validado = (x) => !!(x && typeof x.material_validado === 'object' && x.material_validado);
  const materialOk = esMulti(p) ? lista(p.jornadas).every(validado) : validado(p);
  if (!materialOk) {
    out.push({ clave: 'material', texto: 'Material', detalle: esMulti(p) ? 'Material sin validar en alguna jornada' : 'Material sin validar' });
  }
  if (!p.hora_llegada_estimada) {
    out.push({ clave: 'hora_llegada', texto: 'Llegada', detalle: 'Falta la hora de llegada' });
  }
  return out;
};

// ---------------------------------------------------------------------------
// U-32: respuesta del guardado de la ficha
// ---------------------------------------------------------------------------

// Claves que vienen solo en la respuesta del PUT (avisos): no son del
// documento y no se reenvían (el servidor también las ignora)
export const CLAVES_RESPUESTA_GUARDADO = [
  'campos_conservados', 'adaptaciones_huerfanas', 'aviso_ruta', 'aviso_confirmado_modificado',
  'lineas_automaticas_congeladas', 'motivo_lineas_congeladas', 'lineas_personas_actualizadas',
  'lineas_personas_distintas', 'horas_servicio_desplazadas', 'aviso_horas_servicio',
  'stock_rehecho',
];
const CLAVES_RESPUESTA = new Set(CLAVES_RESPUESTA_GUARDADO);

// Campos que añade el GET de la ficha y no devuelve el PUT
const CAMPOS_SOLO_PANTALLA = ['cliente_data'];
const PREFIJO_LINEA_VIRTUAL = 'lv-';

/**
 * Documento que pinta la ficha tras guardar: el que devuelve el servidor
 * (con el mínimo, Madrid Central, la barra libre, los totales y los
 * `linea_id` que haya puesto), sin las claves de aviso, conservando los
 * campos de solo pantalla de la copia local (`cliente_data`, claves que
 * empiezan por `_`) y el id provisional de las líneas que el servidor guarda
 * sin id (eventos pasados; igual que el GET).
 */
export const documentoTrasGuardado = (servidor, local) => {
  if (!servidor || typeof servidor !== 'object') return local;
  const doc = {};
  Object.keys(servidor).forEach((k) => { if (!CLAVES_RESPUESTA.has(k)) doc[k] = servidor[k]; });
  if (!local || typeof local !== 'object') return doc;
  CAMPOS_SOLO_PANTALLA.forEach((k) => {
    if (!(k in doc) && k in local) doc[k] = local[k];
  });
  Object.keys(local).forEach((k) => {
    if (k.startsWith('_') && !(k in doc)) doc[k] = local[k];
  });
  if (Array.isArray(doc.menus_personalizados) && Array.isArray(local.menus_personalizados)) {
    doc.menus_personalizados = doc.menus_personalizados.map((m, i) => {
      const l = local.menus_personalizados[i];
      if (m && typeof m === 'object' && !m.linea_id && l && typeof l === 'object'
          && typeof l.linea_id === 'string' && l.linea_id.startsWith(PREFIJO_LINEA_VIRTUAL)
          && (l.menu_id || '') === (m.menu_id || '')) {
        return { ...m, linea_id: l.linea_id };
      }
      return m;
    });
  }
  return doc;
};

/**
 * Avisos de la respuesta del PUT que la ficha enseña en un toast (los de
 * confirmado modificado, adaptaciones huérfanas y cambios conservados tienen
 * su propio aviso en la ficha). Devuelve [{ clave, titulo, texto }]; la ficha
 * enseña cada uno una sola vez.
 */
export const avisosRespuestaGuardado = (datos) => {
  if (!datos || typeof datos !== 'object') return [];
  const out = [];
  if (datos.aviso_ruta) out.push({ clave: 'ruta', titulo: 'Ruta sin calcular', texto: String(datos.aviso_ruta) });
  if (datos.lineas_automaticas_congeladas) {
    out.push({
      clave: 'congeladas',
      titulo: 'Líneas automáticas sin recalcular',
      texto: String(datos.motivo_lineas_congeladas || 'Las líneas automáticas de este presupuesto no se recalculan solas.'),
    });
  }
  const n = Number(datos.horas_servicio_desplazadas) || 0;
  if (n > 0 || datos.aviso_horas_servicio) {
    const partes = [];
    if (n > 0) partes.push(`Las horas de ${n} ${n === 1 ? 'servicio' : 'servicios'} se han movido con la hora del evento.`);
    if (datos.aviso_horas_servicio) partes.push(String(datos.aviso_horas_servicio));
    out.push({ clave: 'horas_servicio', titulo: 'Horas de servicio', texto: partes.join(' ') });
  }
  return out;
};

// Campos de coste que el servidor recalcula dentro de cada PUT de la ficha
// (y la marca de cuándo). Tras un autoguardado parcial, la ficha los toma de
// la respuesta para que cambie la firma de costes y se relean el Resumen de
// costes y la Rentabilidad sin recargar la página.
export const CAMPOS_COSTE_RESPUESTA = [
  'updated_at', 'costes_calculado_en', 'coste_total', 'margen', 'beneficio',
  'coste_persona', 'desglose_costes', 'costes_detalle', 'costes_version',
];

/**
 * De un documento devuelto por el servidor, solo los campos de coste (y
 * updated_at) que vengan en él. {} si no es un objeto.
 */
export const camposCosteDeRespuesta = (datos) => {
  if (!datos || typeof datos !== 'object' || Array.isArray(datos)) return {};
  const out = {};
  CAMPOS_COSTE_RESPUESTA.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(datos, k)) out[k] = datos[k];
  });
  return out;
};
